import { UserAttributeKind } from '@hotjar/webapp/domains/user-attributes/model';
import type {
  RadioTextFilterValue,
  UserAttributeType,
  RadioBooleanFilterValue,
  RadioDateFilterValue,
  RadioNumberFilterValue,
} from '@hotjar/global-filters';
import {
  RadioTextFilterOperation,
  RadioBooleanFilterOperation,
  RadioDateFilterOperation,
  RadioNumberFilterOperation,
} from '@hotjar/global-filters';
import { MATCH_OPERATION } from '../TargetingRule/TargetingRule';
import type { TargetingRule } from '../TargetingRule/TargetingRule';
import { UserAttributeDtoKind, UserAttributeKindByStringDtoValue } from './UserAttributesMapping';

type MappedOperation =
  | RadioTextFilterOperation
  | RadioBooleanFilterOperation
  | RadioDateFilterOperation
  | RadioNumberFilterOperation;

interface DtoOperationsWithUIMapping<T extends MappedOperation> {
  uIOperation: T;
  dtoOperation: MATCH_OPERATION;
  dtoNegate: boolean;
}

const STRING_OPERATION_MAPPINGS: DtoOperationsWithUIMapping<RadioTextFilterOperation>[] = [
  {
    uIOperation: RadioTextFilterOperation.CONTAINS,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.CONTAINS,
  },
  {
    uIOperation: RadioTextFilterOperation.NOT_CONTAINS,
    dtoNegate: true,
    dtoOperation: MATCH_OPERATION.CONTAINS,
  },
  {
    uIOperation: RadioTextFilterOperation.IS,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.EXACT,
  },
  {
    uIOperation: RadioTextFilterOperation.IS_NOT,
    dtoNegate: true,
    dtoOperation: MATCH_OPERATION.EXACT,
  },
  {
    uIOperation: RadioTextFilterOperation.STARTS_WITH,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.STARTS_WITH,
  },
  {
    uIOperation: RadioTextFilterOperation.ENDS_WITH,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.ENDS_WITH,
  },
  {
    uIOperation: RadioTextFilterOperation.IS_UNKNOWN,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.UNKNOWN,
  },
  {
    uIOperation: RadioTextFilterOperation.HAS_ANY_VALUE,
    dtoNegate: true,
    dtoOperation: MATCH_OPERATION.UNKNOWN,
  },
];

const BOOLEAN_OPERATION_MAPPINGS: DtoOperationsWithUIMapping<RadioBooleanFilterOperation>[] = [
  {
    uIOperation: RadioBooleanFilterOperation.IS_TRUE,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.EXACT,
  },
  {
    uIOperation: RadioBooleanFilterOperation.IS_FALSE,
    dtoNegate: true,
    dtoOperation: MATCH_OPERATION.EXACT,
  },
  {
    uIOperation: RadioBooleanFilterOperation.IS_UNKNOWN,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.UNKNOWN,
  },
  {
    uIOperation: RadioBooleanFilterOperation.HAS_ANY_VALUE,
    dtoNegate: true,
    dtoOperation: MATCH_OPERATION.UNKNOWN,
  },
];

const DATE_OPERATION_MAPPINGS: DtoOperationsWithUIMapping<RadioDateFilterOperation>[] = [
  {
    uIOperation: RadioDateFilterOperation.IS_ON,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.EXACT_DATE,
  },
  {
    uIOperation: RadioDateFilterOperation.IS_AFTER,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.GREATER_THAN,
  },
  {
    uIOperation: RadioDateFilterOperation.IS_BEFORE,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.LESS_THAN,
  },
  {
    uIOperation: RadioDateFilterOperation.IS_MORE_THAN_DAYS_AGO,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.MORE_THAN_DAYS_AGO,
  },
  {
    uIOperation: RadioDateFilterOperation.IS_EXACTLY_DAYS_AGO,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.EXACTLY_DAYS_AGO,
  },
  {
    uIOperation: RadioDateFilterOperation.IS_LESS_THAN_DAYS_AGO,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.LESS_THAN_DAYS_AGO,
  },
  {
    uIOperation: RadioDateFilterOperation.IS_UNKNOWN,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.UNKNOWN,
  },
  {
    uIOperation: RadioDateFilterOperation.HAS_ANY_VALUE,
    dtoNegate: true,
    dtoOperation: MATCH_OPERATION.UNKNOWN,
  },
];

const NUMBER_OPERATION_MAPPINGS: DtoOperationsWithUIMapping<RadioNumberFilterOperation>[] = [
  {
    uIOperation: RadioNumberFilterOperation.IS_GREATER_THAN,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.GREATER_THAN,
  },
  {
    uIOperation: RadioNumberFilterOperation.IS_LESS_THAN,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.LESS_THAN,
  },
  {
    uIOperation: RadioNumberFilterOperation.IS_EXACTLY,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.EXACT,
  },
  {
    uIOperation: RadioNumberFilterOperation.IS_NOT,
    dtoNegate: true,
    dtoOperation: MATCH_OPERATION.EXACT,
  },
  {
    uIOperation: RadioNumberFilterOperation.IS_UNKNOWN,
    dtoNegate: false,
    dtoOperation: MATCH_OPERATION.UNKNOWN,
  },
  {
    uIOperation: RadioNumberFilterOperation.HAS_ANY_VALUE,
    dtoNegate: true,
    dtoOperation: MATCH_OPERATION.UNKNOWN,
  },
];

export const DATE_COMPARE_OPERATIONS: RadioDateFilterOperation[] = [
  RadioDateFilterOperation.IS_AFTER,
  RadioDateFilterOperation.IS_BEFORE,
  RadioDateFilterOperation.IS_ON,
];

function findMapping<T extends MappedOperation>(
  targetingRule: TargetingRule,
  mappings: DtoOperationsWithUIMapping<T>[],
): DtoOperationsWithUIMapping<T> | undefined {
  return mappings.find(
    (mapping) =>
      mapping.dtoOperation === targetingRule.match_operation &&
      mapping.dtoNegate === targetingRule.negate,
  );
}

export function mapUserAttributeDtoToUIValue(targetingRule: TargetingRule): UserAttributeType {
  const uaValue: UserAttributeType = {
    name: targetingRule.name,
    kind: UserAttributeKindByStringDtoValue[
      targetingRule.rule_type || UserAttributeDtoKind.UNKNOWN
    ],
  };
  switch (targetingRule.rule_type) {
    case UserAttributeDtoKind.STR:
    case UserAttributeDtoKind.EMAIL:
      const stringOperationMapping = findMapping(targetingRule, STRING_OPERATION_MAPPINGS);
      if (stringOperationMapping) {
        uaValue.value = {
          operation: stringOperationMapping.uIOperation,
          value: targetingRule.pattern,
        } as RadioTextFilterValue;
      }
      break;
    case UserAttributeDtoKind.BOOL:
      const booleanOperationMapping = findMapping(targetingRule, BOOLEAN_OPERATION_MAPPINGS);
      if (booleanOperationMapping) {
        uaValue.value = {
          operation: booleanOperationMapping.uIOperation,
        } as RadioBooleanFilterValue;
      }
      break;
    case UserAttributeDtoKind.DATE:
      const dateOperationMapping = findMapping(targetingRule, DATE_OPERATION_MAPPINGS);
      if (dateOperationMapping) {
        uaValue.value = {
          operation: dateOperationMapping.uIOperation,
          value: DATE_COMPARE_OPERATIONS.includes(dateOperationMapping.uIOperation)
            ? new Date((targetingRule.pattern as number) * 1000)
            : targetingRule.pattern,
        } as RadioDateFilterValue;
      }
      break;
    case UserAttributeDtoKind.NUMBER:
      const numberOperationMapping = findMapping(targetingRule, NUMBER_OPERATION_MAPPINGS);
      if (numberOperationMapping) {
        uaValue.value = {
          operation: numberOperationMapping.uIOperation,
          value: targetingRule.pattern,
        } as RadioNumberFilterValue;
      }
      break;
  }
  return uaValue;
}

function findMappingByUIOperation<T extends MappedOperation>(
  uiOperationValue: T,
  mappings: DtoOperationsWithUIMapping<T>[],
): {
  operation: MATCH_OPERATION;
  negate: boolean;
} {
  if (uiOperationValue === undefined) throw new Error('Undefined UI Operation');

  const mapping = mappings.find((m) => m.uIOperation === uiOperationValue);

  if (mapping === undefined) throw new Error('Undefined mapping');

  return {
    operation: mapping.dtoOperation,
    negate: mapping.dtoNegate,
  };
}

export function mapUIOperationToDtoValue(uiOperation: UserAttributeType): {
  operation: MATCH_OPERATION;
  negate: boolean;
} {
  if (uiOperation.value === undefined) throw new Error('Undefined UI operation');

  switch (uiOperation.kind) {
    case UserAttributeKind.String:
    case UserAttributeKind.Email:
      return findMappingByUIOperation(uiOperation.value.operation, STRING_OPERATION_MAPPINGS);
    case UserAttributeKind.Boolean:
      return findMappingByUIOperation(uiOperation.value.operation, BOOLEAN_OPERATION_MAPPINGS);
    case UserAttributeKind.Date:
      return findMappingByUIOperation(uiOperation.value.operation, DATE_OPERATION_MAPPINGS);
    case UserAttributeKind.Number:
      return findMappingByUIOperation(uiOperation.value.operation, NUMBER_OPERATION_MAPPINGS);
  }

  throw new Error('Unknown UI operation');
}
